import {Injectable} from '@angular/core';
import {DomSanitizer, SafeHtml, SafeStyle} from '@angular/platform-browser';
import {LayerUtilService} from './layer-util.service';
import {LookupService} from '../../common/service/lookup.service';
import {SpatialUtilService} from '../services/spatial-util.service';
import {PointSymbologyLookup} from '../../common/models/point-symbology.model';

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
@Injectable({
    providedIn: 'root'
})
export class ConstructPointLayerService {
    private readonly layerId = 'constructPointFeatureLayer_layer';

    private readonly layerSelector = `#${this.layerId}`;

    private readonly featureClass = 'constructpoint';

    private readonly featureIdAttrName = this.layerUtilService.getFeatureIdAttrName(this.featureClass);

    private readonly featureTypeAttrName = this.layerUtilService.getFeatureTypeAttrName(this.featureClass);

    private getPointSymbologyLookups = (): PointSymbologyLookup[] => this.lookupService.getLookups('pointSymbology');

    private getConstructPointSymbologyLookups = (): PointSymbologyLookup[] =>
        this.lookupService.getLookups('pointSymbology').filter((l: PointSymbologyLookup) => [96].includes(l.pointType));

    private svgStyle: string;

    private trustedSvgStyle: SafeStyle;

    constructor(
        private domSanitizer: DomSanitizer,
        private layerUtilService: LayerUtilService,
        private lookupService: LookupService,
        private spatialUtilService: SpatialUtilService
    ) {}

    getSvgStyle(): SafeHtml {
        const pixelScaleFactor = this.layerUtilService.getPixelScaleFactor();
        if (pixelScaleFactor === 0) {
            return '';
        }

        const pointScaleFactor = this.layerUtilService.getPointScaleFactor();

        const mapBackgroundColor = this.spatialUtilService.getMapBackground();

        const showFeatures = this.layerUtilService.showFeatures();

        const defaultBackgroundColor = showFeatures ? (mapBackgroundColor === 'white' ? 'white' : 'black') : 'transparent';

        const newSvgStyle = [
            ...this.getConstructPointSymbologyLookups()
                .filter(l => l.viewMode.toLowerCase() === mapBackgroundColor)
                .map(l => {
                    const foregroundColor =
                        l.customForegroundFontColourRGB && showFeatures ? `rgb(${l.customForegroundFontColourRGB})` : 'transparent';

                    return this.getPointSymbologyLookups()
                        .filter(psl => psl.viewMode.toLowerCase() === mapBackgroundColor)
                        .map(psl => {
                            const fontSizePixels = pixelScaleFactor * psl.foregroundFontSizePts;
                            const circleRadiusPoints = (pointScaleFactor * psl.foregroundFontSizePts) / 2.0;
                            return [
                                `circle[${this.featureTypeAttrName}] {r: ${circleRadiusPoints};}`,

                                `text.nlrp-point-icon[${this.featureTypeAttrName}="${l.pointType}"][data-mark-type="${psl.pointType}"]` +
                                    ` {font-size: ${fontSizePixels}px;}`,

                                `text.nlrp-point-icon[${this.featureTypeAttrName}="${l.pointType}"][data-mark-type="${psl.pointType}"]` +
                                    ` > tspan.nlrp-point-icon-background {fill: ${defaultBackgroundColor};}`,

                                `text.nlrp-point-icon[${this.featureTypeAttrName}="${l.pointType}"][data-mark-type="${psl.pointType}"]` +
                                    ` > tspan.nlrp-point-icon-foreground {fill: ${foregroundColor};}`,

                                `circle[${this.featureTypeAttrName}][data-selected]` +
                                    ` + text.nlrp-point-icon[${this.featureTypeAttrName}="${l.pointType}"]` +
                                    ` > tspan.nlrp-point-icon-foreground {fill:${this.layerUtilService.getSelectedColor()};}`,

                                `circle[${this.featureTypeAttrName}][data-hidden]:not([data-selected])` +
                                    ` + text.nlrp-point-icon[${this.featureTypeAttrName}="${l.pointType}"]` +
                                    ` > tspan.nlrp-point-icon-foreground {fill:${this.layerUtilService.getHiddenColor()};}`,

                                `circle[${this.featureTypeAttrName}]:hover + text.nlrp-point-icon[${this.featureTypeAttrName}="${l.pointType}"]` +
                                    ` > tspan.nlrp-point-icon-foreground {animation: feature-symbol-hover 0.3s alternate ease-in-out none infinite;}`,

                                `circle[${this.featureTypeAttrName}]:not([data-hidden]):not([data-selected]):hover` +
                                    ` + text.nlrp-point-icon[${this.featureTypeAttrName}="${l.pointType}"]` +
                                    ` > tspan.nlrp-point-icon-foreground {fill:${this.layerUtilService.getHoverColor()};}`
                            ]
                                .map(s => `${this.layerSelector} ${s}`)
                                .join('\n');
                        })
                        .join('\n');
                })
        ].join('\n');

        if (newSvgStyle === this.svgStyle) {
            return this.trustedSvgStyle;
        }

        this.svgStyle = newSvgStyle;

        this.trustedSvgStyle = this.domSanitizer.bypassSecurityTrustHtml(this.svgStyle);

        return this.trustedSvgStyle;
    }

    init(): any {
        const constructPointFeatureLayer = this.spatialUtilService.getConstructPointMapLayer();

        this.layerUtilService.addLayerEventHandler(constructPointFeatureLayer, 'graphic-node-add', (evt: any) => {
            const featureId = evt.graphic.attributes.id;
            evt.node.setAttribute('id', this.layerUtilService.getFeatureNodeId(this.featureClass, featureId));
            this.addSymbolNode(evt);
        });

        this.layerUtilService.addLayerEventHandler(constructPointFeatureLayer, 'graphic-node-remove', (evt: any) => {
            document
                .querySelectorAll(`${this.layerSelector} text[${this.featureIdAttrName}="${evt.graphic.attributes.id}"]`)
                .forEach(text => text.parentNode.removeChild(text));
        });

        this.layerUtilService.addLayerEventHandler(constructPointFeatureLayer, 'graphics-clear', () => {
            console.info('constructPointFeatureLayer graphics-clear');
        });

        this.layerUtilService.addLayerEventHandler(constructPointFeatureLayer, 'edits-complete', () => {
            const {pointIds, pointTypes, markTypes} = this.spatialUtilService.getConstructPointGraphics().reduce(
                (acc: any, g: any) => {
                    acc.pointIds.push(g.attributes.id);
                    acc.pointTypes.push(g.attributes.type);
                    acc.markTypes.push(g.attributes.marktype);
                    return acc;
                },
                {pointIds: [], pointTypes: [], markTypes: []}
            );

            document.querySelectorAll(`${this.layerSelector} text[${this.featureIdAttrName}]`).forEach(text => {
                const pointId = parseInt(text.getAttribute(this.featureIdAttrName));
                const pointIdx = pointIds.indexOf(pointId);
                if (pointIdx === -1) {
                    text.parentNode.removeChild(text);
                } else {
                    const pointType = pointTypes[pointIdx];
                    const markType = markTypes[pointIdx];
                    const pointTypeAttrValue = parseInt(text.getAttribute(this.featureTypeAttrName));
                    const markTypeAttrValue = parseInt(text.getAttribute('data-mark-type'));

                    if (pointTypeAttrValue !== pointType && markTypeAttrValue !== markType) {
                        text.setAttribute(this.featureTypeAttrName, `${pointType}`);
                        text.setAttribute('data-mark-type', `${markType}`);

                        const tspanBackground = text.childNodes[0];
                        const tspanForeground = text.childNodes[1];

                        const pointSymbologyLookup = this.getPointSymbologyLookup(markType);

                        tspanBackground.textContent = pointSymbologyLookup.backgroundFontKey || '';
                        tspanForeground.textContent = pointSymbologyLookup.foregroundFontKey;
                    }
                }
            });
        });

        this.layerUtilService.addLayerEventHandler(constructPointFeatureLayer, 'graphic-draw', (evt: any) => {
            const featureId = evt.graphic.attributes.id;

            this.layerUtilService.setHiddenAttr(evt.node, this.featureClass, featureId);

            const {cx, cy} = this.layerUtilService.getGraphicShape(evt.graphic);

            const text: any = document.querySelector(`${this.layerSelector} text[${this.featureIdAttrName}="${featureId}"]`);
            if (text) {
                text.setAttribute('x', `${cx}`);
                text.setAttribute('y', `${cy}`);
                text.childNodes[1].setAttribute('x', `${cx}`);
            } else {
                this.addSymbolNode(evt);
            }
        });

        this.layerUtilService.addLayerEventHandler(constructPointFeatureLayer, 'click', (evt: any) => {
            this.layerUtilService.onClick(this.featureClass, evt);
        });

        this.layerUtilService.addLayerEventHandler(constructPointFeatureLayer, 'dbl-click', (evt: any) => {
            this.layerUtilService.setSelectedAttr(evt.target, this.featureClass, evt.graphic.attributes.id);
        });

        this.layerUtilService.addLayerEventHandler(constructPointFeatureLayer, 'mouse-over', (evt: any) => {
            this.layerUtilService.onMouseOver(this.featureClass, evt);
        });

        this.layerUtilService.addLayerEventHandler(constructPointFeatureLayer, 'mouse-out', (evt: any) => {
            this.layerUtilService.onMouseOut(this.featureClass, evt);
        });

        return constructPointFeatureLayer;
    }

    private getPointSymbologyLookup(markType: number) {
        const lookupViewMode = this.spatialUtilService.getMapBackground() === 'white' ? 'White' : 'Black';
        return this.getPointSymbologyLookups().find(l => l.pointType === markType && l.viewMode === lookupViewMode);
    }

    private addSymbolNode(evt: {graphic: any; node: any}) {
        const featureId = evt.graphic.attributes.id;

        const pointSymbologyLookup = this.getPointSymbologyLookup(evt.graphic.attributes.marktype);

        evt.node.setAttribute(this.featureIdAttrName, `${featureId}`);
        evt.node.setAttribute(this.featureTypeAttrName, `${evt.graphic.attributes.type}`);
        evt.node.setAttribute('data-mark-type', `${evt.graphic.attributes.marktype}`);
        this.layerUtilService.setSelectedAttr(evt.node, this.featureClass, featureId);

        evt.node.setAttribute('style', 'fill-opacity:0;stroke-opacity:0');

        const {cx, cy} = this.layerUtilService.getGraphicShape(evt.graphic);

        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.setAttribute(this.featureIdAttrName, `${featureId}`);
        text.setAttribute(this.featureTypeAttrName, `${evt.graphic.attributes.type}`);
        text.setAttribute('data-mark-type', `${evt.graphic.attributes.marktype}`);
        text.setAttribute('class', 'nlrp-point-icon');
        text.setAttribute('text-anchor', 'middle');
        text.setAttribute('pointer-events', 'none');
        text.setAttribute('x', `${cx}`);
        text.setAttribute('y', `${cy}`);

        const tspanBackground = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
        tspanBackground.setAttribute('class', 'nlrp-point-icon-background');
        tspanBackground.textContent = pointSymbologyLookup.backgroundFontKey || '';
        text.appendChild(tspanBackground);

        const tspanForeground = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
        tspanForeground.setAttribute('class', 'nlrp-point-icon-foreground');
        tspanForeground.setAttribute('x', `${cx}`);
        tspanForeground.textContent = pointSymbologyLookup.foregroundFontKey;
        text.appendChild(tspanForeground);

        document.getElementById(this.layerId).appendChild(text);
    }
}
